type ApiErrorDetail = {
  code?: string;
  errorCode?: string;
};

type ApiErrorLike = {
  data?: {
    errorDetails?: ApiErrorDetail | ApiErrorDetail[];
  };
  response?: {
    data?: {
      errorDetails?: ApiErrorDetail | ApiErrorDetail[];
    };
  };
  errorDetails?: ApiErrorDetail | ApiErrorDetail[];
};

export type TranslateFn = (key: string) => string;
export type ExistsFn = (key: string) => boolean;

export function extractApiErrorCode(error: unknown): string | null {
  if (!error || typeof error !== "object") {
    return null;
  }

  const errorObj = error as ApiErrorLike;
  const errorDetails =
    errorObj.response?.data?.errorDetails ??
    errorObj.data?.errorDetails ??
    errorObj.errorDetails;

  const detail = Array.isArray(errorDetails) ? errorDetails[0] : errorDetails;
  if (!detail) {
    return null;
  }

  return detail.errorCode ?? detail.code ?? null;
}

export function getRegisterErrorMessageFromCode(
  error: unknown,
  t: TranslateFn,
  exists: ExistsFn,
): string {
  const code = extractApiErrorCode(error);

  if (code) {
    const registerKey = `auth.register.errorcodes.${code}`;
    if (exists(registerKey)) {
      return t(registerKey);
    }

    const authKey = `auth.errorCodes.${code}`;
    if (exists(authKey)) {
      return t(authKey);
    }

    const rootKey = `errorCodes.${code}`;
    if (exists(rootKey)) {
      return t(rootKey);
    }
  }

  return t("auth.error.generic");
}

export const PERSONAL_INFO_MISMATCH_ERROR_CODES = [
  "CUST_S1200013",
  "BR_U2600006",
] as const;

export type PersonalInfoSubstep = "input" | "password";

export function isPersonalInfoMismatchError(code: string | null): boolean {
  return (
    code !== null &&
    (PERSONAL_INFO_MISMATCH_ERROR_CODES as readonly string[]).includes(code)
  );
}

export function handleRegisterPersonalInfoMismatchError(
  errorCode: string | null,
  message: string,
  setPersonalInfoSubstep: (substep: PersonalInfoSubstep) => void,
  setStatusMessage: (msg: string | null) => void,
): boolean {
  if (!isPersonalInfoMismatchError(errorCode)) {
    return false;
  }

  setPersonalInfoSubstep("input");
  setStatusMessage(message);
  return true;
}

export type RegisterOtpValidationResult =
  | null
  | { status: "BANNER_ONLY" }
  | { status: "CLEAR_CODE" }
  | { status: "ALREADY_VALIDATED" };

export function handleRegisterOtpValidationError(
  errorCode: string | null,
  message: string,
  setStatusMessage: (msg: string | null) => void,
  setOtpError: (msg: string | null) => void,
): RegisterOtpValidationResult {
  if (errorCode === "CUST_S1000006") {
    setStatusMessage(message);
    setOtpError(null);
    return { status: "BANNER_ONLY" };
  }

  if (errorCode === "CUST_S1000007") {
    setStatusMessage(null);
    setOtpError(message);
    return null;
  }

  if (errorCode === "CUST_S1000009") {
    setStatusMessage(message);
    setOtpError(null);
    return { status: "CLEAR_CODE" };
  }

  if (errorCode === "CUST_S1000010") {
    setStatusMessage(message);
    setOtpError(null);
    return { status: "ALREADY_VALIDATED" };
  }

  setStatusMessage(null);
  setOtpError(message);
  return null;
}
